import React, { useEffect, useMemo, useRef, useState } from 'react';
import DraftFields, { formError, fromForm, toForm } from './DraftFields';
import DraftImages from './DraftImages';
import DuplicatePanel from './DuplicatePanel';
import ProductPreviewModal from './ProductPreviewModal';
import SiblingPanel from './SiblingPanel';
import VariantsEditor from './VariantsEditor';
import { single } from './variantRows';
import { errorMessage, recheckSimilarity, updateItem, updateItemStatus } from '../../api/catalogImportApi';
import { btnDanger, btnGhost, btnPrimary, card, pill } from './styles';

const STATUS_PILL = {
  PENDING_REVIEW: { text: 'Needs review', style: pill('#FEF3E2', '#B15400') },
  CREATED: { text: 'Created', style: pill('#DCFCE7', '#166534') },
  LINKED: { text: 'Existing product', style: pill('#DBEAFE', '#1E40AF') },
  MERGED: { text: 'Merged', style: pill('#F3F4F6', '#6B7280') },
  DISCARDED: { text: 'Discarded', style: pill('#F3F4F6', '#6B7280') },
};

/**
 * One AI-drafted product. Edits stay local until Save; "Create product" saves first, so what the
 * reviewer sees is exactly what gets created. Server-side warnings reflect the last saved state.
 */
/** @param campaign  the import's Brandstore campaign ({ id, name, ... }) or null for a plain import */
export default function DraftCard({ item, campaign, loadPages, brands, categories, selected, onToggleSelect, onItemChanged, onCreate, onDirtyChange, onReload }) {
  const saved = useMemo(() => toForm(item), [item]);
  const [form, setForm] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [previewForm, setPreviewForm] = useState(null); // form values the open preview was built from

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const editable = item.status === 'PENDING_REVIEW';

  // When the server version changes (save, image edit, list reload), adopt it — unless the
  // reviewer has unsaved typing relative to the previous version, which must not be wiped out.
  const prevSaved = useRef(saved);
  useEffect(() => {
    const before = JSON.stringify(prevSaved.current);
    prevSaved.current = saved;
    setForm((current) => (JSON.stringify(current) === before ? saved : current));
  }, [saved]);
  useEffect(() => { onDirtyChange(item.id, dirty); }, [item.id, dirty, onDirtyChange]);

  const setField = (f, v) => setForm((prev) => ({ ...prev, [f]: v }));

  // recheck=false when Create follows straight after: the backend checks before creating anyway.
  const save = async ({ recheck = true } = {}) => {
    const bad = formError(form);
    if (bad) { setError(bad); return null; }
    setBusy(true);
    setError('');
    try {
      const res = await updateItem(item.id, fromForm(form));
      onItemChanged(res.data);
      // A changed name/brand/category can mean different look-alikes: re-ask the AI right away so
      // the reviewer never decides on verdicts about the old details. Doesn't block the save.
      if (recheck && res.data.similarityStatus === 'STALE') {
        recheckSimilarity(item.id).then((r) => onItemChanged(r.data)).catch(() => {});
      }
      return res.data;
    } catch (err) {
      setError(errorMessage(err, 'Could not save the draft.'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const current = dirty ? await save({ recheck: false }) : item;
    if (!current) return;
    setBusy(true);
    try {
      await onCreate(current);
    } finally {
      setBusy(false);
    }
  };

  // Previews the form as it is now, saved or not — nothing is written.
  const openPreview = () => {
    const bad = formError(form);
    if (bad) { setError(bad); return; }
    setError('');
    setPreviewForm(fromForm(form));
  };

  const setStatus = async (status) => {
    setBusy(true);
    setError('');
    try {
      const res = await updateItemStatus(item.id, status);
      onItemChanged(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not update the draft.'));
    } finally {
      setBusy(false);
    }
  };

  const brandIsNew = form.brandName.trim() !== ''
    && !brands.some((b) => b.name.toLowerCase() === form.brandName.trim().toLowerCase());
  const status = STATUS_PILL[item.status] || STATUS_PILL.PENDING_REVIEW;
  // Unsaved edits may change the look-alikes, so only block when the saved draft is what's checked.
  const blockedByDuplicate = item.duplicateDecisionNeeded && !dirty;

  return (
    <article style={{ ...card, opacity: item.status === 'DISCARDED' ? 0.7 : 1, borderColor: selected ? '#F29213' : '#E5E7EB' }}
             aria-label={`Draft: ${item.name || 'unnamed product'}`}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {editable && (
          <input type="checkbox" checked={selected} onChange={() => onToggleSelect(item.id)}
                 aria-label={`Select ${item.name || 'draft'} for creation`} style={{ width: 18, height: 18, accentColor: '#F29213' }} />
        )}
        <strong style={{ fontSize: 15, color: '#111827' }}>{item.name || 'Unnamed product'}</strong>
        <span style={status.style}>{status.text}</span>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>Page {item.pageNumber}</span>
        {brandIsNew && editable && <span style={pill('#EDE9FE', '#5B21B6')}>New brand</span>}
        {dirty && <span style={pill('#FEF9C3', '#854D0E')}>Unsaved changes</span>}
        {item.status === 'LINKED' && item.productId && (
          <span style={{ fontSize: 12, color: '#1E40AF', marginLeft: 'auto' }}>
            Used existing <a href={`/product/${item.productId}`} target="_blank" rel="noreferrer" style={{ fontWeight: 600, color: '#1E40AF' }}>product #{item.productId} ↗</a>
            {item.updatedFields ? ` — updated ${item.updatedFields}` : ' as is'}
            {campaign ? ' · added to campaign' : ''}
          </span>
        )}
        {item.status === 'MERGED' && item.mergedIntoItemId && (
          <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 'auto' }}>Now an option of draft #{item.mergedIntoItemId}</span>
        )}
        {item.status === 'CREATED' && item.productId && (
          <a href={`/product/${item.productId}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#F29213', fontWeight: 600, marginLeft: 'auto' }}>
            View product #{item.productId} ↗
          </a>
        )}
      </header>

      {editable && <DuplicatePanel item={item} campaign={campaign} onItemChanged={onItemChanged} onError={setError} />}
      {editable && <SiblingPanel key={(item.siblings || []).map((s) => s.itemId).join(',')} item={item}
                                 onItemChanged={onItemChanged} onReload={onReload} onError={setError} />}
      {editable && item.warnings?.length > 0 && (
        <div role="status" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>
            Check before creating{dirty ? ' (save to re-check)' : ''}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#78350F' }}>
            {item.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}
      {item.aiNotes && editable && (
        <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#075985' }}>
          <strong style={{ fontSize: 12 }}>AI notes: </strong>{item.aiNotes}
        </div>
      )}
      {error && <div role="alert" style={{ color: '#B91C1C', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ marginBottom: 16 }}>
        <DraftImages item={item} loadPages={loadPages} disabled={!editable} onItemChanged={onItemChanged} onError={setError} />
      </div>

      <DraftFields itemId={item.id} form={form} setField={setField} disabled={!editable || busy}
                   brands={brands} categories={categories} suggestedGst={item.suggestedGst} />

      {(editable || form.variants.length > 0) && (
        <details open={form.variants.length > 0} style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#111827' }}>
            Options (variants){form.variants.length ? ` — ${form.variants.length}` : ' — none, a simple product'}
          </summary>
          <div style={{ marginTop: 10 }}>
            <VariantsEditor rows={form.variants} onChange={(rows) => setField('variants', rows)} images={item.images || []}
                            disabled={!editable || busy} mopSet={form.marketOperatingPrice.trim() !== ''} idPrefix={`draft-${item.id}`}
                            newRow={{ mrp: form.mrp, eyantraPurchasePrice: form.eyantraPurchasePrice,
                                      color: form.variants.length ? '' : single(form.colors), size: form.variants.length ? '' : single(form.sizes) }} />
          </div>
        </details>
      )}

      <footer style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
        {item.status === 'DISCARDED' && <button style={btnGhost} disabled={busy} onClick={() => setStatus('PENDING_REVIEW')}>Restore</button>}
        {editable && (
          <>
            <button style={btnDanger} disabled={busy} onClick={() => setStatus('DISCARDED')}>Discard</button>
            <button style={btnGhost} disabled={busy || item.images.length === 0} onClick={openPreview}
                    title={item.images.length === 0 ? 'Add an image to preview' : 'See it as it will look in the shop'}>
              Preview in shop
            </button>
            <button style={{ ...btnGhost, opacity: dirty ? 1 : 0.6 }} disabled={busy || !dirty} onClick={() => save()}>Save</button>
            <button style={{ ...btnPrimary, opacity: busy || blockedByDuplicate ? 0.6 : 1 }} disabled={busy || blockedByDuplicate} onClick={create}
                    title={blockedByDuplicate ? 'Decide whether this is an existing product first' : undefined}>
              {busy ? 'Working…' : `${dirty ? 'Save & create' : 'Create'} product${campaign ? ' & add to campaign' : ''}`}
            </button>
          </>
        )}
      </footer>

      {previewForm && <ProductPreviewModal item={item} unsavedForm={previewForm} onClose={() => setPreviewForm(null)} />}
    </article>
  );
}
